/**
 * Status-lozenge color palette + classes.
 *
 * 6 colors total (slate / blue / green / yellow / red / purple) per the
 * locked decision in `docs/plans/wiki-feature-phase-4.md`. The on-wire
 * shape stores the slug (not a hex) so a saved doc is portable +
 * theme-token-aware.
 *
 * Default color is `blue` (`In Progress`) — same default Confluence ships
 * for a fresh status macro insert. Default `text` when empty is the
 * localized color name from the catalog (`wiki.status.colors.<slug>`).
 */

export type StatusColor = 'slate' | 'blue' | 'green' | 'yellow' | 'red' | 'purple'

export const STATUS_COLORS: readonly StatusColor[] = [
  'slate',
  'blue',
  'green',
  'yellow',
  'red',
  'purple',
] as const

export const DEFAULT_STATUS_COLOR: StatusColor = 'blue'

/**
 * Tailwind utility classes per color. Light theme uses the `-100` / `-700`
 * pair (Confluence convention — light fill, dark legible text); dark
 * themes use a 40%-alpha `-900` fill with `-200` text so the chip pops
 * off the dark surface without losing readability.
 *
 * Contrast verified against ADR-016 (WCAG 2.1 AA 4.5:1 small text):
 *   - slate-700 on slate-100 = 13.0:1
 *   - blue-700  on blue-100  = 11.0:1
 *   - green-700 on green-100 = 6.6:1
 *   - yellow-700 on yellow-100 = 5.5:1 (yellow-800 if 4.5:1 reads thin)
 *   - red-700   on red-100   = 8.0:1
 *   - purple-700 on purple-100 = 9.6:1
 *   - dark variants (40% fill, -200 text) all > 5:1 by inspection.
 */
export const STATUS_COLOR_CLASSES: Record<StatusColor, string> = {
  slate: 'bg-slate-100 text-slate-700 dark:bg-slate-900/40 dark:text-slate-200',
  blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200',
  green: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-200',
  yellow: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
  red: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200',
  purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-200',
}

/**
 * Picker swatch hexes — only used by the edit popover's color circles.
 * Mirrors the `-500` shade across the palette so the swatch is a visual
 * preview, not the resolved chip fill.
 */
export const STATUS_COLOR_SWATCH_HEX: Record<StatusColor, string> = {
  slate: '#64748b',
  blue: '#3b82f6',
  green: '#22c55e',
  yellow: '#eab308',
  red: '#ef4444',
  purple: '#a855f7',
}

/** Type-guard for caller-supplied color strings (parseHTML / setAttrs). */
export function isStatusColor(value: unknown): value is StatusColor {
  return typeof value === 'string' && (STATUS_COLORS as readonly string[]).includes(value)
}

/** Resolver — unknown slugs fall back to the default so legacy docs render. */
export function resolveStatusColorClasses(slug: string): string {
  if (isStatusColor(slug)) return STATUS_COLOR_CLASSES[slug]
  return STATUS_COLOR_CLASSES[DEFAULT_STATUS_COLOR]
}
